import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { gql } from 'graphql-request';

import {
  GraphQLRequestWithTokenProps,
  requestWithTokenRequired,
} from '@repo/ui/src/utils/graphql-request';
import { SelfFragment, useCustomer, useCustomerIdToken } from '@hooks/customer';
import useSWRMutation from 'swr/mutation';
import { imgToJPG } from '@utils/image';
import axios from 'axios';

interface Input {
  firstName: string;
  lastName: string;
  phone?: string;
  avatar: File;
  smsSubscribed?: boolean;
  emailSubsribed?: boolean;
}

const UPDATE_SELF = gql`
  ${SelfFragment}
  mutation UpdateSelf($input: UpdateUserInput!) {
    updateSelf(input: $input) {
      ...Self
    }
  }
`;

const UPLOAD_AVATAR = gql`
  mutation UploadAvatar($input: UploadAvatarRequest!) {
    uploadAvatar(input: $input) {
      url
    }
  }
`;

const fetcher = async (
  { document, locale, idToken }: GraphQLRequestWithTokenProps,
  {
    arg,
  }: {
    arg: Input;
  },
) => {
  if (arg?.avatar && typeof arg?.avatar === 'object') {
    const avatarData = await requestWithTokenRequired({
      document: UPLOAD_AVATAR,
      idToken,
      locale,
      variables: {
        input: { imageType: 'image/jpeg' },
      },
    });
    if (!avatarData) {
      return;
    }
    const imgBlob = await imgToJPG(arg.avatar);

    await axios({
      url: avatarData.uploadAvatar.url,
      method: 'PUT',
      data: imgBlob,
      headers: {
        'Content-Type': 'image/jpeg',
      },
    });

    const url = new URL(avatarData.uploadAvatar.url);
    const timestamp = new Date().getTime();
    const newUrl = `${url.origin}${url.pathname}?v=${timestamp}`;

    const data = await requestWithTokenRequired({
      document,
      idToken,
      locale,
      variables: {
        input: {
          ...arg,
          avatar: newUrl,
        },
      },
    });

    return { ...data.updateSelf, avatar: newUrl };
  }
  const data = await requestWithTokenRequired({
    document,
    idToken,
    locale,
    variables: {
      input: arg,
    },
  });
  return { ...data.updateSelf, avatar: arg.avatar };
};

export const useUpdateCustomer = () => {
  const { mutate } = useCustomer();
  const idToken = useCustomerIdToken();
  const { locale } = useCurrentUrl();

  return useSWRMutation({ document: UPDATE_SELF, idToken, locale }, fetcher, {
    onSuccess: (result) => {
      mutate(result, { revalidate: !result });
    },
  });
};
